import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBillingDetails,
  activateSubscription,
  confirmSubscription,
  getOrganisationProducts,
  getInvoices,
  addPaymentMethod,
  confirmPaymentMethod,
  dismissBillingFailureNotice,
  retryInvoicePayment,
  updateBillingDetails,
  cancelSubscription,
  previewSubscription,
  getFeatureFlagProduct,
} from "../../api/BillingApi";
import { useToast } from "../../context/ToastContext";

// Get billing details (customer, payment methods, subscription)
export function useBillingDetails(payingCustomerId = null) {
  return useQuery({
    queryKey: ["billing-details", payingCustomerId],
    queryFn: () => getBillingDetails(payingCustomerId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
}

// Get organisation's billing products
export function useBillingProducts() {
  return useQuery({
    queryKey: ["billing-products"],
    queryFn: getOrganisationProducts,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Get invoices with pagination
export function useInvoices(limit = 10, startingAfter = null, payingCustomerId = null) {
  return useQuery({
    queryKey: ["billing-invoices", limit, startingAfter, payingCustomerId],
    queryFn: () => getInvoices(limit, startingAfter, payingCustomerId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!startingAfter || limit <= 10, // Fetch when starting_after is provided or initial load
  });
}

// Activate subscription
export const useActivateSubscription = () => {
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();

  return useMutation({
    mutationKey: ["activate-subscription"],
    mutationFn: (data) => activateSubscription(data),
    onSuccess: () => {
      // Don't show toast here - handled in modal for better control
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing-products"],
      });
    },
    onError: (error) => {
      setToastMessage("Failed to activate subscription");
      console.error("Subscription activation error:", error);
    },
  });
};

// Confirm subscription after 3D Secure authentication
export const useConfirmSubscription = () => {
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();

  return useMutation({
    mutationKey: ["confirm-subscription"],
    mutationFn: (data) => confirmSubscription(data),
    onSuccess: () => {
      // Don't show toast here - handled in modal for better control
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing-products"],
      });
    },
    onError: (error) => {
      setToastMessage("Failed to confirm subscription");
      console.error("Subscription confirmation error:", error);
    },
  });
};

// Add payment method
export const useAddPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["add-payment-method"],
    mutationFn: (data) => addPaymentMethod(data),
    onSuccess: () => {
      // Don't show toast here - let the component handle it based on response
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
    },
    onError: (error) => {
      // Don't show toast here either - let component handle it
    },
  });
};

export const useConfirmPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["confirm-payment-method"],
    mutationFn: (data) => confirmPaymentMethod(data),
    onSuccess: () => {
      // Don't show toast here - let the component handle it
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
    },
    onError: (error) => {
      // Don't show toast here either - let component handle it
    },
  });
};

export const useDismissBillingFailureNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["dismiss-billing-failure-notice"],
    mutationFn: (data) => dismissBillingFailureNotice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing-products"],
      });
    },
  });
};

export const useRetryInvoicePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["retry-invoice-payment"],
    mutationFn: (data) => retryInvoicePayment(data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-details"] });
      queryClient.invalidateQueries({ queryKey: ["billing-invoices"] });
    },
  });
};

// Update billing details
export const useUpdateBillingDetails = () => {
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();

  return useMutation({
    mutationKey: ["update-billing-details"],
    mutationFn: (data) => updateBillingDetails(data),
    onSuccess: () => {
      setToastMessage("Billing details updated successfully!");
      // Invalidate billing details to refetch updated customer info
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
    },
  });
};

// Cancel subscription
export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();

  return useMutation({
    mutationKey: ["cancel-subscription"],
    mutationFn: (payingCustomerId = null) => cancelSubscription(payingCustomerId),
    onSuccess: () => {
      setToastMessage("Subscription will be canceled at the end of the billing period");
      // Invalidate billing details to refetch updated subscription status
      queryClient.invalidateQueries({
        queryKey: ["billing-details"],
      });
    },
    onError: (error) => {
      setToastMessage("Failed to cancel subscription");
      console.error("Cancel subscription error:", error);
    },
  });
};

// Preview subscription with tax calculation
export const useTaxPreview = (country, postalCode, payingCustomerId) => {
  return useQuery({
    queryKey: ["tax-preview", country, postalCode || '', payingCustomerId || ''],
    queryFn: async () => {
      const payload = { country, postal_code: postalCode || '' };
      if (payingCustomerId) payload.paying_customer_id = payingCustomerId;
      const result = await previewSubscription(payload);
      return result.data;
    },
    enabled: Boolean(country), // Only need country now
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    refetchOnMount: true,
  });
};

// Get feature flag product details
export const useFeatureFlagProduct = (featureFlagReference) => {
  return useQuery({
    queryKey: ["feature-flag-product", featureFlagReference],
    queryFn: async () => {
      const result = await getFeatureFlagProduct(featureFlagReference);
      return result.data;
    },
    enabled: Boolean(featureFlagReference),
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
  });
};
